import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { toast } from "react-toastify";
import { routeOptimizationService, IJobResponse, IOptimizationJob } from "services/routeOptimizationService";
import { dateToday } from "utils/dateToday";

interface IOptimizationJobsContext {
  jobs: IJobResponse;
  loading: boolean;
  error: string | null;
  refreshJobs: () => Promise<void>;
  getJobsByDepot: (depotName: string) => IOptimizationJob[];
  getAllJobs: () => IOptimizationJob[];
  lastUpdated: Date | null;
}

const OptimizationJobsContext = createContext<IOptimizationJobsContext>({} as IOptimizationJobsContext);

interface OptimizationJobsProviderProps {
  children: ReactNode;
  intervalTime?: number; // em milissegundos, padrão 30 segundos
}

export function OptimizationJobsProvider({
  children,
  intervalTime = 15000 // 15 segundos por padrão
}: OptimizationJobsProviderProps) {
  const [jobs, setJobs] = useState<IJobResponse>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchJobs = async (isInitialLoad = false) => {
    try {
      // Só mostra loading na primeira carga
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const tomorrowDate = dateToday();
      const response = await routeOptimizationService.getJobs({
        executionDate: tomorrowDate
      });

      setJobs(response);
      setLastUpdated(new Date());
    } catch (err: any) {
      const errorMessage = err?.message ?? "Erro ao buscar jobs de otimização";
      setError(errorMessage);
      console.error("Erro ao buscar jobs de otimização:", err);

      // Só mostra toast se não for a primeira vez carregando
      if (lastUpdated) {
        toast.error(errorMessage);
      }
    } finally {
      // Só remove loading se estava mostrando
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const refreshJobs = async () => {
    await fetchJobs();
  };

  const getJobsByDepot = (depotName: string): IOptimizationJob[] => {
    return jobs[depotName] || [];
  };

  const getAllJobs = (): IOptimizationJob[] => {
    return Object.values(jobs).flat();
  };

  // useEffect(() => {
  //   // Busca inicial com loading
  //   fetchJobs(true);

  //   // Configura o interval para atualizações automáticas (sem loading)
  //   const intervalId = setInterval(() => fetchJobs(false), intervalTime);

  //   // Cleanup do interval quando o componente for desmontado
  //   return () => {
  //     clearInterval(intervalId);
  //   };
  // }, [intervalTime]);

  const contextValue: IOptimizationJobsContext = {
    jobs,
    loading,
    error,
    refreshJobs,
    getJobsByDepot,
    getAllJobs,
    lastUpdated
  };

  return (
    <OptimizationJobsContext.Provider value={contextValue}>
      {children}
    </OptimizationJobsContext.Provider>
  );
}

export const useOptimizationJobs = () => {
  const context = useContext(OptimizationJobsContext);

  if (!context) {
    throw new Error("useOptimizationJobs deve ser usado dentro de OptimizationJobsProvider");
  }

  return context;
};
